import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { vehiclePurchasableExtrasQuery } from "@/app/Vehicles/GraphQL/vehicleRecordQuery";
import { purchasableExtrasQuery } from "@/app/Suppliers/GraphQL/supplierPurchasableExtrasQuery";
import { applyVehiclePurchasableExtrasMutation } from "@/app/Vehicles/GraphQL/vehicleMutations";
import { Switch } from "@/components/catalyst/switch";
import { formatPurchasableExtraPrice } from "@/app/Suppliers/Utils/formatPurchasableExtraPrice";
import {
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";
import { RelocationType } from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierPurchasableExtraCreateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraCreateDialog";
import { SupplierPurchasableExtraUpdateDialog } from "@/app/Suppliers/Components/SupplierPurchasableExtraUpdateDialog";
import { IconButton } from "@/lib/Components/Button/IconButton";

export function RelocationPurchasableExtrasCard() {
  const record = useRelocationRecord();
  const relocationType = record.type;
  const vehicleId = record.vehicle.id;
  const supplierId = record.supplier.id;

  const { mutateAsync } = useGqlMutation(applyVehiclePurchasableExtrasMutation);

  const { data: vehicleExtras } = useSuspenseGqlQuery(
    vehiclePurchasableExtrasQuery,
    { vehicleId },
  );

  const { data } = useSuspenseGqlQuery(purchasableExtrasQuery, {
    first: 200,
    page: 1,
    supplierId,
  });

  const { open: openCreate } = useDialog(SupplierPurchasableExtraCreateDialog);
  const { open: openEdit } = useDialog(SupplierPurchasableExtraUpdateDialog);

  const extras = data.supplierPurchasableExtras.data;
  const productLabel =
    relocationType === RelocationType.GapRental ? "gap rental" : "relocation";

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-lg bg-emerald-100 p-2">
          <ShoppingBagIcon className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <div className="font-semibold text-gray-900">Optional extras</div>
          <p className="text-sm font-normal text-gray-500">
            Extras a customer can buy with this {productLabel}. Toggling one
            updates {record.vehicle.name} for every {productLabel} using it, not
            just this listing.
          </p>
        </div>
      </div>

      {extras.length === 0 ? (
        <p className="mb-4 text-sm italic text-gray-500">
          This supplier has no extras yet.
        </p>
      ) : (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {extras.map((extra) => {
            const enabled = vehicleExtras.vehicle.vehiclePurchasableExtras.some(
              (e) =>
                e.supplierPurchasableExtra.id === extra.id &&
                e.relocation_type === relocationType,
            );

            return (
              <div
                key={extra.id}
                className="rounded-lg border border-gray-200 bg-gray-50 p-4"
              >
                <div className="flex items-center gap-3">
                  {extra.image ? (
                    <img
                      src={extra.image.url}
                      alt={extra.name}
                      className="size-12 shrink-0 rounded-md border border-gray-200 bg-white object-cover"
                    />
                  ) : (
                    <div className="flex size-12 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 bg-white">
                      <PhotoIcon className="size-5 text-gray-300" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {extra.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatPurchasableExtraPrice(
                        extra.unit_price,
                        extra.supplier.currency,
                      )}
                    </div>
                  </div>

                  <IconButton
                    Icon={PencilSquareIcon}
                    tooltip="Edit extra"
                    onClick={() =>
                      openEdit({
                        supplierPurchasableExtra: extra,
                        currency: extra.supplier.currency,
                      })
                    }
                  />

                  <Switch
                    color="blue"
                    checked={enabled}
                    aria-label={`${extra.name} available`}
                    name={`${extra.name}-${relocationType}`}
                    onChange={async (changed) => {
                      await mutateAsync({
                        vehicleId,
                        supplierPurchasableExtraId: extra.id,
                        enabled: changed,
                        relocationType,
                      });
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => openCreate({ supplierId })}
        className="flex items-center gap-2 text-sm font-semibold text-gray-500 transition hover:text-indigo-600"
      >
        <PlusIcon className="size-5" />
        Add extra
      </button>
    </div>
  );
}
